/**
 * The Memo tab: the section rail on the left, the memo page in the middle
 * and "How to read this memo" on the right (DOP-616).
 *
 * The rail says in words where to look before reading: all sourced, a check
 * (a conflict), or a gap (missing or omitted). The page is the memo as the
 * firm will read it: title block, the company facts, then every section in
 * order. Every sourced sentence ends in its
 * source number, which opens the page it came from.
 *
 * Code-written sections carry no prose: the financial summary is rendered from
 * the facts and the diligence questions from the questions.
 */

import { Info } from "lucide-react";
import React, { useMemo, useState } from "react";

import {
  type DealScreeningFact,
  type DealScreeningParagraph,
  type DealScreeningRun,
  type DealScreeningSection,
  type DealScreeningSource,
  memoDealName,
  reviewBlockedReason,
} from "../../types/dealScreening";
import MemoLegend from "./MemoLegend";
import { formatIsoDay, memoTicker } from "./dealScreeningFormat";
import { baseReading } from "./enteredDiff";
import FactValue from "./FactValue";
import FinancialSummaryTable from "./FinancialSummaryTable";
import { indexFacts } from "./DerivedFormulaPopover";
import HeaderBlockTable from "./HeaderBlockTable";
import { renderInline, stripMarks } from "./inlineMarks";
import MemoSentence from "./MemoSentence";
import MemoChart from "./MemoChart";
import MemoTable from "./MemoTable";
import { VersionName } from "./memoVersionLabel";
import {
  QUESTIONS_SECTION_KEY,
  type SectionDot,
  companyFactsTitle,
  factElementId,
  factIndex,
  memoSections,
  sectionDot,
  sectionElementId,
  sectionNumbers,
  sentenceCaseHeading,
  sourceNumbers,
} from "./memoModel";
import { SourceNumberContext } from "./sourceNumbering";

export interface MemoViewProps {
  run: DealScreeningRun;
  onOpenSource: (source: DealScreeningSource) => void;
  /** The section the reader last chose; the Analyst's "this section". */
  activeSectionKey: string | null;
  onSelectSection: (key: string) => void;
  /** Reopens a person's own answer for editing (DOP-555). */
  onEditFact?: (fact: DealScreeningFact) => void;
  /** Opens an open conflict on the Gaps tab, where a person chooses. */
  onOpenConflict?: (fact: DealScreeningFact) => void;
  /** A company fact whose other readings open unfolded: a source's jump lands there. */
  expandFactKey?: string | null;
}

export { sectionElementId } from "./memoModel";

/**
 * The info icon beside a person's edit (DOP-580): hover for the summary,
 * click for the card with the name and the email address.
 */
const EditInfo: React.FC<{
  label: string;
  email: string | null;
  testId: string;
  /** What could not be re-applied on a rerun, in plain words (DOP-580). */
  notes?: string[];
}> = ({ label, email, testId, notes = [] }) => {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative ml-1 inline-flex align-middle font-sans text-[11px] font-normal normal-case tracking-normal">
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          setOpen((was) => !was);
        }}
        title={label}
        aria-label={label}
        aria-expanded={open}
        className="inline-flex text-[#5B3FA8]/70 hover:text-[#5B3FA8]"
        data-testid={testId}
      >
        <Info className="h-3.5 w-3.5" aria-hidden />
      </button>
      {open && (
        <span
          role="dialog"
          className="absolute left-0 top-5 z-10 w-max max-w-[320px] rounded border border-[#5B3FA8]/30 bg-white px-2.5 py-1.5 text-left text-[12px] leading-4 text-text-base shadow"
          data-testid={`${testId}-card`}
        >
          <span className="block text-[#5B3FA8]">{label}</span>
          {email && <span className="block text-text-muted">{email}</span>}
          {notes.length > 0 && (
            <ul
              className="mt-1 list-disc space-y-0.5 border-t border-gray-100 pl-4 pt-1 text-[#7a4b00]"
              data-testid="section-edit-notes"
            >
              {notes.map((note) => (
                <li key={note}>{note}</li>
              ))}
            </ul>
          )}
        </span>
      )}
    </span>
  );
};

/** The rail's status for a section, in words: colour is never the only cue. */
const DOT_WORD: Record<
  SectionDot,
  { text: string; className: string; label: string }
> = {
  sourced: { text: "✓", className: "text-[#067647]", label: "all sourced" },
  conflict: {
    text: "Check",
    className: "text-[#b54708]",
    label: "has a conflict",
  },
  gap: {
    text: "Gap",
    className: "text-[#b42318]",
    label: "has a gap or omitted",
  },
};

interface ParagraphsProps {
  paragraphs: DealScreeningParagraph[];
  index: Map<string, DealScreeningFact[]>;
  onOpenSource: (source: DealScreeningSource) => void;
}

/**
 * Prose, sub-headings, section-8 bullets (a paragraph with a lead-in), and —
 * since DOP-533 — a bullets paragraph (one bullet per sentence) or a table
 * paragraph (no sentences; the table's cells are sourced like sentences).
 */
const Paragraphs: React.FC<ParagraphsProps> = ({
  paragraphs,
  index,
  onOpenSource,
}) => {
  const factsFor = (keys: string[]) =>
    keys.flatMap((key) => index.get(key) ?? []);
  const sentence = (
    item: DealScreeningParagraph["sentences"][number],
    edited = false,
    edit: DealScreeningParagraph["edit"] = null,
  ) => (
    <MemoSentence
      key={item.id}
      sentence={item}
      facts={factsFor(item.fact_keys)}
      onOpenSource={onOpenSource}
      edited={edited}
      noteBy={edit?.by_name ?? null}
      noteAt={edit?.at ?? null}
    />
  );
  // A strength or risk that is the writer's judgement over cited facts says
  // so beside its lead (DOP-657); its sentences keep their sources.
  const analystTag = (paragraph: DealScreeningParagraph) =>
    paragraph.basis === "analyst" ? (
      <span
        className="ml-1.5 inline-block rounded-full border border-[#d6d9de] px-1.5 align-[1px] font-sans text-[10.5px] font-medium leading-4 text-[#5B6472]"
        title="The analyst's judgement, drawn from the cited facts"
        data-testid="analyst-view"
      >
        Analyst view
      </span>
    ) : null;
  // Only the sentences a person wrote read as theirs; a cited sentence
  // they left standing keeps its citation and colour.
  const isEdited = (
    paragraph: DealScreeningParagraph,
    item: DealScreeningParagraph["sentences"][number],
  ) => item.edited ?? (paragraph.edit ? item.source === null : false);
  const body = (paragraph: DealScreeningParagraph) =>
    paragraph.sentences.map((item) =>
      sentence(item, isEdited(paragraph, item), paragraph.edit),
    );
  // A person's paragraph reads in its own colour; who and when sit behind
  // an info icon, not in the text (DOP-580).
  const mark = (paragraph: DealScreeningParagraph) => {
    if (!paragraph.edit) return null;
    const who = [
      paragraph.edit.op === "insert" ? "Added" : "Edited",
      paragraph.edit.by_name ? `by ${paragraph.edit.by_name}` : null,
      paragraph.edit.at ? `on ${formatIsoDay(paragraph.edit.at)}` : null,
    ]
      .filter(Boolean)
      .join(" ");
    return (
      <EditInfo
        label={who}
        email={paragraph.edit.by_email ?? null}
        testId="paragraph-edit-mark"
      />
    );
  };
  const render = (paragraph: DealScreeningParagraph, key: string) => {
    if (paragraph.kind === "chart" && paragraph.chart) {
      return (
        <>
          <MemoChart
            chart={paragraph.chart}
            idPrefix={key}
            onOpenSource={onOpenSource}
            noteBy={paragraph.edit?.by_name ?? null}
          />
          {mark(paragraph)}
        </>
      );
    }
    if (paragraph.kind === "table" && paragraph.table) {
      return (
        <MemoTable
          table={paragraph.table}
          idPrefix={key}
          factsFor={factsFor}
          onOpenSource={onOpenSource}
          noteBy={paragraph.edit?.by_name ?? null}
          noteAt={paragraph.edit?.at ?? null}
        />
      );
    }
    if (paragraph.kind === "bullets" && paragraph.ordered) {
      // A list a person numbered (DOP-641).
      return (
        <ol
          className="list-decimal space-y-0.5 pl-6"
          data-testid="memo-numbered"
        >
          {paragraph.lead && (
            <li className="-ml-6 list-none">
              <strong
                className={paragraph.lead_edited ? "text-[#5B3FA8]" : undefined}
              >
                {paragraph.lead}
              </strong>
              {analystTag(paragraph)}
            </li>
          )}
          {paragraph.sentences.map((item, position) => (
            <li key={item.id}>
              {sentence(item, isEdited(paragraph, item), paragraph.edit)}
              {position === paragraph.sentences.length - 1 && mark(paragraph)}
            </li>
          ))}
        </ol>
      );
    }
    if (paragraph.kind === "bullets") {
      return (
        <ul className="space-y-0.5 pl-4" data-testid="memo-bullets">
          {paragraph.lead && (
            <li className="relative before:absolute before:-left-4 before:content-['•']">
              <strong
                className={paragraph.lead_edited ? "text-[#5B3FA8]" : undefined}
              >
                {paragraph.lead}
              </strong>
              {analystTag(paragraph)}
            </li>
          )}
          {paragraph.sentences.map((item, position) => (
            <li
              key={item.id}
              className="relative before:absolute before:-left-4 before:content-['•']"
            >
              {sentence(item, isEdited(paragraph, item), paragraph.edit)}
              {position === paragraph.sentences.length - 1 && mark(paragraph)}
            </li>
          ))}
        </ul>
      );
    }
    if (paragraph.lead) {
      return (
        <p className="relative pl-4 before:absolute before:left-0 before:content-['•']">
          <strong
            className={paragraph.lead_edited ? "text-[#5B3FA8]" : undefined}
          >
            {paragraph.lead}
          </strong>
          {analystTag(paragraph)} {body(paragraph)}
          {mark(paragraph)}
        </p>
      );
    }
    return (
      <p>
        {body(paragraph)}
        {mark(paragraph)}
      </p>
    );
  };
  let heading: string | null = null;
  return (
    <div className="space-y-2">
      {paragraphs.map((paragraph, position) => {
        const showHeading =
          paragraph.sub_heading !== null && paragraph.sub_heading !== heading;
        heading = paragraph.sub_heading ?? heading;
        const key = paragraph.sentences[0]?.id ?? `p${position}`;
        return (
          <React.Fragment key={key}>
            {showHeading && (
              <h4 className="pt-1 text-[13.5px] font-bold leading-[21px] text-[#1f2937]">
                {renderInline(paragraph.sub_heading ?? "", sentenceCaseHeading)}
              </h4>
            )}
            {render(paragraph, key)}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const MemoView: React.FC<MemoViewProps> = ({
  run,
  onOpenSource,
  activeSectionKey,
  onSelectSection,
  onEditFact,
  onOpenConflict,
  expandFactKey = null,
}) => {
  // Editing is the page's edit mode (DOP-622); a section only shows who
  // edited it and can show what Doppler first wrote.
  const edited = (section: DealScreeningSection) =>
    Boolean(section.edit) &&
    section.written_by === "model" &&
    section.key !== QUESTIONS_SECTION_KEY &&
    section.status === "written" &&
    !run.generating;
  const index = useMemo(() => factIndex(run.facts), [run.facts]);
  const numbers = useMemo(() => sourceNumbers(run), [run]);
  const sections = useMemo(() => memoSections(run.sections), [run.sections]);
  const sectionNos = useMemo(() => sectionNumbers(sections), [sections]);
  const questions = useMemo(
    () => [...run.questions].sort((a, b) => a.sort_order - b.sort_order),
    [run.questions],
  );

  const goTo = (key: string) => {
    onSelectSection(key);
    document.getElementById(sectionElementId(key))?.scrollIntoView?.({
      behavior: "smooth",
      block: "start",
    });
  };

  const statusLabel = run.status === "reviewed" ? "Reviewed" : "Draft";
  // DOP-534: the analyst's title block carries a one-line tagline under the
  // company name; it is a classified header field, shown here and not as a row.
  const tagline = run.facts.find(
    (fact) =>
      fact.key === "tagline" && typeof fact.value === "string" && fact.value,
  );
  const ticker = memoTicker(run.facts, memoDealName(run));
  const factsBySlot = React.useMemo(() => indexFacts(run.facts), [run.facts]);
  // The entity type chooses the figure spine (DOP-531): the run's reasoning,
  // not a header row the firm's memos carry.
  const headerFacts = run.facts.filter(
    (fact) =>
      fact.key !== "entity_type" && !(tagline && fact.key === "tagline"),
  );

  const renderBody = (section: DealScreeningSection) => {
    // A section the run left out that a person wrote is theirs (DOP-637).
    if (section.status === "omitted" && section.paragraphs.length === 0) {
      return (
        <p className="italic text-text-muted">
          Omitted
          {section.omitted_reason
            ? ` — ${section.omitted_reason.replace(/\.\s*$/, "")}`
            : ""}
          .{/* A run asked twice already says what to do in its own reason. */}
          {/edit mode/i.test(section.omitted_reason ?? "")
            ? null
            : section.appears === "always"
              ? " It is set to appear in every memo: write it in edit mode, or add files and generate the memo again."
              : " Add files that cover it and generate the memo again; nothing is filled from general knowledge."}
        </p>
      );
    }
    if (section.key === "financial_summary") {
      return (
        <FinancialSummaryTable
          facts={run.facts}
          figures={run.figures}
          conventions={run.conventions}
          onOpenSource={onOpenSource}
          onEditFact={onEditFact}
          onOpenConflict={onOpenConflict}
        />
      );
    }
    if (
      section.key === QUESTIONS_SECTION_KEY &&
      section.paragraphs.length === 0
    ) {
      return (
        <ol
          className="list-decimal space-y-1.5 pl-5"
          data-testid="diligence-questions"
        >
          {questions.map((question) => (
            <li key={`${question.sort_order}:${question.topic}`}>
              <strong>{question.topic}.</strong>{" "}
              {/* The question is the link, as every memo sentence is
							    (DOP-556), underlined on hover like prose; a gap's
							    question has no passage and is plain. */}
              {question.source ? (
                <MemoSentence
                  sentence={{
                    id: `diligence_questions:${question.sort_order}`,
                    text: question.text,
                    source: question.source,
                    fact_keys: [],
                  }}
                  facts={[]}
                  onOpenSource={onOpenSource}
                />
              ) : (
                question.text
              )}
              {question.critical && (
                <span className="ml-1.5 font-sans text-[11px] font-semibold text-[#b42318]">
                  CRITICAL
                </span>
              )}
            </li>
          ))}
        </ol>
      );
    }
    return (
      <Paragraphs
        paragraphs={section.paragraphs}
        index={index}
        onOpenSource={onOpenSource}
      />
    );
  };

  // A person's words already read in their colour, each paragraph with who
  // and when behind its own icon (DOP-637). The section says more only when
  // an edit did not re-apply as written: where it went, or that it did not.
  const controls = (section: DealScreeningSection) => {
    if (
      !edited(section) ||
      !section.edit ||
      (section.edit.notes ?? []).length === 0
    )
      return null;
    return (
      <span className="ml-2 inline-flex items-center gap-2 font-sans text-[11px] font-normal normal-case tracking-normal">
        <EditInfo
          label={`Edited${section.edit.edited_by_name ? ` by ${section.edit.edited_by_name}` : ""} on ${formatIsoDay(section.edit.edited_at)}${
            section.edit.carried_from_version !== null
              ? ` · carried from v${section.edit.carried_from_version}`
              : ""
          }`}
          email={section.edit.edited_by_email ?? null}
          notes={section.edit.notes}
          testId="section-edited-by"
        />
      </span>
    );
  };

  return (
    <SourceNumberContext.Provider value={numbers}>
      <div className="flex items-start gap-6">
        <nav
          aria-label="Memo sections"
          className="sticky top-0 hidden w-52 shrink-0 lg:block"
        >
          <p className="mb-2 px-2.5 text-[11px] font-semibold uppercase tracking-[.09em] text-[#9a9ea3]">
            In this memo
          </p>
          <ol className="space-y-px">
            {sections.map((section) => {
              const dot = sectionDot(section, run, index);
              return (
                <li key={section.key}>
                  <button
                    type="button"
                    onClick={() => goTo(section.key)}
                    aria-current={
                      activeSectionKey === section.key ? "true" : undefined
                    }
                    className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-[7px] text-left text-[13px] leading-[18px] ${
                      activeSectionKey === section.key
                        ? "bg-white font-semibold text-[#004C4B] shadow-[0_0_0_1px_#e7e7e4]"
                        : "text-[#3f4247] hover:bg-black/[0.04]"
                    }`}
                  >
                    <span className="w-[18px] shrink-0 text-xs tabular-nums text-[#9a9ea3]">
                      {sectionNos.get(section.key)}
                    </span>
                    <span className="min-w-0 flex-1 truncate">
                      {stripMarks(section.title)}
                    </span>
                    <span
                      className={`shrink-0 font-sans text-[11px] font-semibold ${DOT_WORD[dot].className}`}
                      title={DOT_WORD[dot].label}
                      aria-label={DOT_WORD[dot].label}
                    >
                      {DOT_WORD[dot].text}
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
          <div className="mt-4">
            <MemoLegend
              reviewBlockedReason={
                run.status === "reviewed" ? null : reviewBlockedReason(run)
              }
            />
          </div>
        </nav>

        <article
          className="min-w-0 max-w-[900px] flex-1 rounded-xl border border-[#e7e7e4] bg-white px-10 py-8 font-serif text-[15px] leading-[1.65] text-[#1c1d1f]"
          data-testid="memo-page"
        >
          {/* A report cover, not an app header: serif name, plain summary, a rule. */}
          <header>
            <p className="font-sans text-[11px] font-medium uppercase tracking-[.08em] text-[#8a8f98]">
              {run.firm_name ? `${run.firm_name} · ` : ""}Opportunity Memo ·{" "}
              {statusLabel}
              {run.name ? " · " : " "}
              <VersionName version={run.version} name={run.name} />
            </p>
            <h2 className="mt-2 font-serif text-[40px] font-bold leading-[1.1] tracking-[-0.015em] text-[#111111]">
              {memoDealName(run)}
              {/* A listed company's name carries where it trades (DOP-661). */}
              {ticker && (
                <span
                  className="ml-2 align-middle font-sans text-[15px] font-normal tracking-normal text-[#6B7280]"
                  data-testid="memo-ticker"
                >
                  {" "}
                  ({ticker})
                </span>
              )}
            </h2>
            {tagline && (
              <p
                className={`mt-2 max-w-[62ch] font-serif text-[16.5px] leading-[1.5] ${
                  // A tagline a person typed reads in their colour (DOP-641).
                  // One they only changed keeps the document's words in the
                  // document's colour, so the change itself stands out.
                  (tagline.entered_by_user_id || tagline.entered_by_name) &&
                  !baseReading(tagline)
                    ? "text-[#5B3FA8]"
                    : "text-[#3f4247]"
                }`}
                data-testid="memo-tagline"
                id={factElementId("tagline")}
              >
                {/* A fact like the ones below it (DOP-659): its source opens,
							    whether a file's or the one a person named. Other wordings
							    stay off the cover, but the fact keeps them so a person's
							    edit shows only their words as theirs (DOP-658). */}
                <FactValue
                  fact={tagline}
                  variant="text"
                  onOpenSource={onOpenSource}
                  onEdit={onEditFact}
                  hideOtherWordings
                />
              </p>
            )}
          </header>

          {/* The rule the cover ends on; the date is in the page header (DOP-659). */}
          <div className="mt-4 border-t border-[#ececea] pt-5">
            <HeaderBlockTable
              title={companyFactsTitle(run)}
              facts={headerFacts}
              dealName={memoDealName(run)}
              onOpenSource={onOpenSource}
              onEditFact={onEditFact}
              onOpenConflict={onOpenConflict}
              factsBySlot={factsBySlot}
              expandFactKey={expandFactKey}
            />
          </div>

          {sections.map((section) => (
            <section
              key={section.key}
              id={sectionElementId(section.key)}
              className="mt-8 scroll-mt-4 border-t border-[#ececea] pt-6"
              aria-labelledby={`${sectionElementId(section.key)}-title`}
            >
              <h3
                id={`${sectionElementId(section.key)}-title`}
                className="mb-3 flex flex-wrap items-baseline gap-x-2 font-serif text-[22px] font-bold leading-snug tracking-[-0.005em] text-[#004C4B]"
              >
                {/* A span of its own: the heading is a flex row, and a bold
                    word in it must not become a column (DOP-660). */}
                <span>{renderInline(section.title)}</span>
                {controls(section)}
              </h3>
              {renderBody(section)}
            </section>
          ))}
        </article>
      </div>
    </SourceNumberContext.Provider>
  );
};

export default MemoView;
